import React from 'react';
import {
  alacartePricing,
  secondCleaningOffer,
  transparentPricingSummary,
} from '../data/pricing';
import CherryFinancing from './CherryFinancing';

type Props = {
  /** Show intro headings (use false when embedded under an existing section title) */
  showTitles?: boolean;
  className?: string;
};

export const AlacartePricingTable: React.FC<{ className?: string }> = ({ className = '' }) => {
  const a = alacartePricing;
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full text-left border-collapse min-w-[320px]">
        <thead>
          <tr className="border-b-2 border-gray-200">
            <th className="py-3 pr-4 text-xs font-bold tracking-widest text-gray-800 uppercase">Service</th>
            <th className="py-3 pr-4 text-xs font-bold tracking-widest text-gray-800 uppercase hidden sm:table-cell">Details</th>
            <th className="py-3 text-right text-xs font-bold tracking-widest text-gray-800 uppercase whitespace-nowrap">Price</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          <tr className="border-b border-gray-100 align-top">
            <td className="py-3 pr-2 font-semibold">{a.doctorExam.label}</td>
            <td className="py-3 pr-4 text-sm text-gray-600 hidden sm:table-cell">
              <span className="font-semibold text-gray-800">{a.doctorExam.displayPrice}</span>
              <p className="mt-2 text-xs leading-snug text-gray-500">{a.doctorExam.finePrint}</p>
            </td>
            <td className="py-3 text-right font-bold whitespace-nowrap align-top">{a.doctorExam.displayPrice}</td>
          </tr>
          <tr className="border-b border-gray-100 sm:hidden">
            <td colSpan={3} className="py-2 text-xs text-gray-500 leading-snug">
              {a.doctorExam.finePrint}
            </td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="py-3 pr-2 font-semibold">{a.bloodWork.label}</td>
            <td className="py-3 pr-4 text-sm text-gray-600 hidden sm:table-cell">{a.bloodWork.detail}</td>
            <td className="py-3 text-right font-bold whitespace-nowrap">${a.bloodWork.price}</td>
          </tr>
          <tr className="border-b-2 border-gray-300 align-top">
            <td className="py-3 pr-2 font-semibold">{a.dentalCleaning.label}</td>
            <td className="py-3 pr-4 text-sm text-gray-600 hidden sm:table-cell">
              <ul className="list-disc pl-4 space-y-1">
                {a.dentalCleaning.includes.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </td>
            <td className="py-3 text-right font-bold whitespace-nowrap align-top">
              <div>${a.dentalCleaning.cat} <span className="text-gray-500 font-normal text-sm">cat</span></div>
              <div className="mt-1">${a.dentalCleaning.dog} <span className="text-gray-500 font-normal text-sm">dog</span></div>
            </td>
          </tr>
          <tr className="border-b border-gray-100 sm:hidden">
            <td colSpan={3} className="py-2 text-sm text-gray-600">
              <span className="font-semibold text-gray-800">Includes: </span>
              {a.dentalCleaning.includes.join(' · ')}
            </td>
          </tr>

          <tr>
            <td
              colSpan={3}
              className="pt-8 pb-3 text-xs font-bold tracking-widest text-gray-800 uppercase bg-gray-50 border-y border-gray-200"
            >
              Additional potential costs
            </td>
          </tr>
          {a.extractions.map((e) => (
            <tr key={e.label} className="border-b border-gray-100">
              <td className="py-2 pr-2 pl-4 sm:pl-0 text-sm font-medium text-gray-700">Extractions — {e.label}</td>
              <td className="py-2 pr-4 hidden sm:table-cell" />
              <td className="py-2 text-right font-bold whitespace-nowrap">${e.price}</td>
            </tr>
          ))}
          <tr className="border-b border-gray-100">
            <td className="py-3 pr-2 font-semibold">{a.periovive.label}</td>
            <td className="py-3 pr-4 text-sm text-gray-600 hidden sm:table-cell">{a.periovive.detail}</td>
            <td className="py-3 text-right font-bold whitespace-nowrap">${a.periovive.price}</td>
          </tr>
          <tr>
            <td className="py-3 pr-2 font-semibold">{a.postOpMedications.label}</td>
            <td className="py-3 pr-4 text-sm text-gray-600 hidden sm:table-cell">{a.postOpMedications.detail}</td>
            <td className="py-3 text-right font-bold whitespace-nowrap">
              ${a.postOpMedications.min}–${a.postOpMedications.max}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/** Transparent pricing summary + second-cleaning prepaid offer */
export const TransparentPricingAndOffer: React.FC<{ className?: string }> = ({ className = '' }) => {
  const pricing = transparentPricingSummary;
  const offer = secondCleaningOffer;
  return (
    <div className={`grid md:grid-cols-2 gap-6 md:gap-0 md:divide-x md:divide-gray-200 ${className}`}>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 md:rounded-r-none md:border-r-0">
        <h4 className="text-center text-lg font-bold tracking-wide uppercase border-b-2 border-gray-800 pb-2 mb-2">
          {pricing.name}
        </h4>
        <p className="text-center text-sm text-gray-600 mb-6">{pricing.subtitle}</p>
        <ul className="space-y-4 text-sm">
          {pricing.rows.map((row) => (
            <li key={row.service} className="flex justify-between gap-4 border-b border-gray-100 pb-3 last:border-0">
              <span className="font-medium text-gray-800 uppercase tracking-wide text-xs">{row.service}</span>
              <span className="text-gray-900 font-semibold text-right shrink-0">{row.value}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 pt-4 border-t-2 border-gray-200 flex flex-col gap-2">
          <div className="flex justify-between items-baseline gap-2">
            <span className="font-bold uppercase tracking-wide text-sm">{pricing.totalLabel}</span>
            <span className="text-lg sm:text-xl font-bold text-right">{pricing.total}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1 pt-3 border-t border-gray-100">
            <span className="font-bold uppercase tracking-wide text-xs text-gray-700 shrink-0">
              {pricing.twiceYearlyLabel}
            </span>
            <span className="text-sm font-bold text-gray-900 text-right leading-snug">{pricing.twiceYearlyValue}</span>
          </div>
          <p className="text-xs text-gray-500 leading-snug">{pricing.footnote}</p>
        </div>
      </div>

      <div className="bg-teal-50/70 rounded-2xl border border-teal-100 p-6 md:p-8 md:rounded-l-none md:border-l-0 flex flex-col justify-center">
        <p className="text-center text-xs font-bold tracking-widest uppercase text-brand-teal mb-3">
          {offer.eyebrow}
        </p>
        <h4 className="text-center text-lg font-bold tracking-wide uppercase border-b-2 border-gray-800 pb-2 mb-4">
          {offer.title}
        </h4>
        <p className="text-center text-base text-gray-700 leading-relaxed mb-8">
          {offer.body}
        </p>
        <div className="text-center mt-auto pt-4 border-t border-teal-200/80">
          <p className="text-3xl font-bold text-gray-900">{offer.highlight}</p>
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-teal mt-1">
            {offer.highlightDetail}
          </p>
        </div>
      </div>
    </div>
  );
};

/** @deprecated Use TransparentPricingAndOffer */
export const AnnualPlansComparison = TransparentPricingAndOffer;

/**
 * Full block: itemized table + transparent pricing / second-cleaning offer + Cherry.
 */
const PricingSections: React.FC<Props> = ({ showTitles = true, className = '' }) => {
  return (
    <div className={className}>
      {showTitles && (
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Totally transparent pricing</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Start with the three line items below to estimate a typical cleaning visit, then review additional services only if your pet needs them.
          </p>
        </div>
      )}

      <div
        id="pricing-tldr"
        className="mb-10 max-w-3xl mx-auto rounded-2xl border border-brand-teal/20 bg-brand-teal/5 px-5 py-4 text-left"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">Quick answer</p>
        <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
          Dental cleaning is <strong>$495 for cats</strong> and <strong>$595 for dogs</strong> (includes anesthesia,
          X-rays, scaling/charting/polishing). Bloodwork is $150. The doctor exam is complimentary when you proceed
          with recommended care. Core visits typically start around <strong>$645 (cat)</strong> /{' '}
          <strong>$745 (dog)</strong>. Save $150 on a prepaid second cleaning within 12 months. Financing via Cherry.
        </p>
      </div>

      <AlacartePricingTable className="mb-16" />

      {showTitles && (
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            What a typical visit looks like
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Published line-item rates — plus a prepaid savings option for pet parents who want to stay ahead of dental disease.
          </p>
        </div>
      )}
      <TransparentPricingAndOffer />

      <div className="mt-10">
        <CherryFinancing />
      </div>
    </div>
  );
};

export default PricingSections;
